class GngMinimumProgress extends HTMLElement {
  connectedCallback() {
    this.minimum = Number(this.dataset.minimumCents || 20000);
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.progressTemplate = this.dataset.progressTemplate || '[percent]% • [current] of [minimum]';
    this.successText = this.dataset.successText || "Minimum reached — you're ready to checkout.";
    this.refresh();

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate && !this.unsubscribe) {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event?.cartData || event?.cart || event;
        if (cartData && typeof cartData.total_price === 'number') {
          this.update(cartData.total_price, cartData.item_count || 0);
        } else {
          this.refresh();
        }
        scheduleGngProgressRefreshes();
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: this.currency,
    }).format(cents / 100);
  }

  buildText(total, percent) {
    if (total >= this.minimum) return this.successText;

    return this.progressTemplate
      .replaceAll('[percent]', String(Math.round(percent)))
      .replaceAll('[current]', this.formatMoney(total))
      .replaceAll('[minimum]', this.formatMoney(this.minimum));
  }

  async refresh() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) return;
      const cart = await response.json();
      this.update(cart.total_price || 0, cart.item_count || 0);
    } catch (error) {
      console.warn('GNG progress update failed', error);
    }
  }

  update(total, itemCount) {
    const percent = this.minimum > 0
      ? Math.min(100, Math.max(0, (total / this.minimum) * 100))
      : 100;

    const text = this.buildText(total, percent);
    const wrapper = this.closest('.gng-global-progress-wrap');
    if (wrapper) wrapper.style.setProperty('--gng-progress', `${percent}%`);

    const baseText = this.querySelector('[data-gng-progress-text]');
    const fillText = this.querySelector('[data-gng-progress-fill-text]');
    if (baseText) baseText.textContent = text;
    if (fillText) fillText.textContent = text;

    document.querySelectorAll('#checkout, #CartDrawer-Checkout').forEach((button) => {
      button.disabled = itemCount === 0 || total < this.minimum;
    });

    const dynamic = document.getElementById('GngDynamicCheckoutButtons');
    if (dynamic) dynamic.hidden = total < this.minimum;
  }
}

if (!customElements.get('gng-minimum-progress')) {
  customElements.define('gng-minimum-progress', GngMinimumProgress);
}

function refreshGngProgressBars(delay = 0) {
  window.setTimeout(() => {
    document.querySelectorAll('gng-minimum-progress').forEach((element) => element.refresh());
  }, delay);
}

// Product-page quantity tables can save asynchronously. A single refresh may run
// before Shopify has committed the first addition or final removal, so recheck
// the cart at a few short intervals and let the last server response win.
function scheduleGngProgressRefreshes() {
  [150, 450, 900, 1600].forEach((delay) => refreshGngProgressBars(delay));
}

function updateGngStickyOffset() {
  const header = document.querySelector('.section-header');
  if (!header) {
    document.documentElement.style.setProperty('--gng-sticky-header-offset', '0px');
    return;
  }

  const isSticky = header.classList.contains('shopify-section-header-sticky');
  const isHidden = header.classList.contains('shopify-section-header-hidden');
  const offset = isSticky && !isHidden ? header.getBoundingClientRect().height : 0;
  document.documentElement.style.setProperty('--gng-sticky-header-offset', `${Math.max(0, offset)}px`);
}

document.addEventListener('change', (event) => {
  if (event.target.matches('input[name="updates[]"], .quantity__input')) scheduleGngProgressRefreshes();
});

document.addEventListener('input', (event) => {
  if (event.target.matches('input[name="updates[]"], .quantity__input')) scheduleGngProgressRefreshes();
});

document.addEventListener('click', (event) => {
  if (event.target.closest('quantity-input button, cart-remove-button, .quantity__button, product-form button[type="submit"]')) {
    scheduleGngProgressRefreshes();
  }
});

window.addEventListener('pageshow', () => scheduleGngProgressRefreshes());
window.addEventListener('resize', updateGngStickyOffset);
window.addEventListener('scroll', updateGngStickyOffset, { passive: true });
document.addEventListener('DOMContentLoaded', () => {
  updateGngStickyOffset();
  const header = document.querySelector('.section-header');
  if (header) {
    new MutationObserver(updateGngStickyOffset).observe(header, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    new ResizeObserver(updateGngStickyOffset).observe(header);
  }
});
